import { apiSlice } from "../../app/api/apiSlice";
import { ApiResponse, UserObject } from "../../shared/types";

type NewUserData = {
    name: string;
    email: string;
    password: string;
    passwordConfirm: string;
    role: string;
};

type UpdatePasswordData = {
    passwordCurrent: string;
    password: string;
    passwordConfirm: string;
};

export const usersApiSlice = apiSlice.injectEndpoints({
    endpoints: builder => ({
        getUsers: builder.query<ApiResponse<UserObject[]>, string>({
            query: (params: string) => ({ url: `/api/v1/users?${params}` }),
            keepUnusedDataFor: 10,
            providesTags: (result) =>
                result?.data?.data
                    ? [
                          { type: "User" as const, id: "LIST" },
                          ...result.data.data.map((user) => ({
                              type: "User" as const,
                              id: user._id,
                          })),
                      ]
                    : [{ type: "User" as const, id: "LIST" }],
        }),
        getUser: builder.query<ApiResponse<UserObject>, string>({
            query: (id: string) => ({ url: `/api/v1/users/${id}` }),
            keepUnusedDataFor: 10,
            providesTags: (result, error, id) => [{ type: "User" as const, id }],
        }),
        getCurrentUser: builder.query<ApiResponse<UserObject>, string>({
            query: () => ({ url: `/api/v1/users/me` }),
            keepUnusedDataFor: 10,
            providesTags: [{ type: "User" as const, id: "LIST" }],
        }),
        addNewUser: builder.mutation<ApiResponse<UserObject>, NewUserData>({
            query: initialUserData => ({
                url: "/api/v1/users/signup",
                method: "POST",
                body: { ...initialUserData },
            }),
            invalidatesTags: [{ type: "User", id: "LIST" }],
        }),
        updateUser: builder.mutation<ApiResponse<UserObject>, FormData>({
            query: userData => ({
                url: `/api/v1/users/updateMe`,
                method: "PATCH",
                body: userData,
                formData: true,
            }),
            invalidatesTags: [{ type: "User", id: "LIST" }],
        }),
        updatePassword: builder.mutation<ApiResponse<UserObject>, UpdatePasswordData>({
            query: initialUserData => ({
                url: `/api/v1/users/updateMyPassword`,
                method: "PATCH",
                body: { ...initialUserData },
            }),
            invalidatesTags: [{ type: "User", id: "LIST" }],
        }),
        deleteUser: builder.mutation<unknown, { id: string }>({
            query: ({ id }) => ({
                url: `/users`,
                method: "DELETE",
                body: { id },
            }),
            invalidatesTags: (result, error, arg) => [
                { type: "User", id: arg.id },
            ],
        }),
    }),
})

export const {
    useGetUsersQuery,
    useGetUserQuery,
    useGetCurrentUserQuery,
    useAddNewUserMutation,
    useUpdateUserMutation,
    useDeleteUserMutation,
    useUpdatePasswordMutation,
} = usersApiSlice
